import React, { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Typography,
  Chip,
  IconButton,
} from "@mui/material";
import { Add as AddIcon, Close as CloseIcon } from "@mui/icons-material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import PageHeader from "../../components/layout/PageHeader";
import DataListCard from "../../components/layout/DataListCard";
import voucherAPI, {
  CreateVoucherData,
  VoucherType,
} from "../../services/voucherAPI";
import { branchAPI } from "../../services/branchAPI";
import { salesPointAPI } from "../../services/salesPointAPI";
import { useAuthStore } from "../../stores/authStore";
import { useRBAC } from "../../hooks/useRBAC";
import { indiaFyMeta } from "../../services/financialYearAPI";

const formatCurrency = (value: number) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 2,
  }).format(Number(value) || 0);

const typeColor = (t: string) => {
  if (t === "receipt") return "success";
  if (t === "payment") return "warning";
  return "default";
};

const VouchersPage: React.FC = () => {
  const queryClient = useQueryClient();
  const { user } = useAuthStore();
  const { canCreate } = useRBAC();
  const role = String(user?.role || "").toUpperCase();
  const isAdmin = role === "ADMIN";

  const fy = indiaFyMeta();
  const [startDate, setStartDate] = useState(fy.startDate);
  const [endDate, setEndDate] = useState(() => {
    const today = format(new Date(), "yyyy-MM-dd");
    return today < fy.endDate ? today : fy.endDate;
  });
  const [branchId, setBranchId] = useState(
    isAdmin ? user?.branchId || "" : ""
  );
  const [salesPointId, setSalesPointId] = useState(user?.salesPointId || "");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [typeFilter, setTypeFilter] = useState<string>("");
  const [open, setOpen] = useState(false);
  const [alert, setAlert] = useState<{
    type: "success" | "error";
    message: string;
  } | null>(null);

  const [form, setForm] = useState<CreateVoucherData>({
    voucherType: "receipt",
    voucherDate: format(new Date(), "yyyy-MM-dd"),
    branchId: isAdmin ? user?.branchId || "" : "",
    salesPointId: user?.salesPointId || "",
    partyName: "",
    amount: 0,
    paymentMethod: "cash",
    narration: "",
  });

  const { data: branches = [] } = useQuery({
    queryKey: ["branchesAll"],
    queryFn: () => branchAPI.getAllBranches(),
  });

  const { data: salesPoints = [] } = useQuery({
    queryKey: ["salesPointsAll"],
    queryFn: () => salesPointAPI.getAllSalesPoints(),
  });

  const filteredSalesPoints = useMemo(() => {
    const bid = branchId || form.branchId;
    if (!bid) return salesPoints;
    return salesPoints.filter((s) => s.branchId === bid);
  }, [salesPoints, branchId, form.branchId]);

  const listParams = {
    startDate,
    endDate,
    branchId: branchId || undefined,
    salesPointId: salesPointId || undefined,
    voucherType: typeFilter || undefined,
  };

  const { data: vouchers = [], isLoading, error } = useQuery({
    queryKey: ["vouchers", listParams],
    queryFn: () => voucherAPI.list(listParams),
  });

  const { mutate: createVoucher, isPending: saving } = useMutation({
    mutationFn: (payload: CreateVoucherData) => voucherAPI.create(payload),
    onSuccess: (row) => {
      queryClient.invalidateQueries({ queryKey: ["vouchers"] });
      setOpen(false);
      setAlert({
        type: "success",
        message: `Voucher ${row.voucherNumber} created.`,
      });
    },
    onError: (err: any) => {
      setAlert({
        type: "error",
        message: err.response?.data?.message || "Failed to create voucher",
      });
    },
  });

  const handleCreate = () => {
    setAlert(null);
    if (!form.branchId || !form.salesPointId) {
      setAlert({
        type: "error",
        message: "Branch and sales point are required.",
      });
      return;
    }
    if (!Number.isFinite(form.amount) || form.amount <= 0) {
      setAlert({ type: "error", message: "Enter a positive amount." });
      return;
    }
    createVoucher({
      ...form,
      partyName: form.partyName?.trim() || undefined,
      narration: form.narration?.trim() || undefined,
    });
  };

  const openCreate = () => {
    setForm({
      voucherType: "receipt",
      voucherDate: format(new Date(), "yyyy-MM-dd"),
      branchId: branchId || (isAdmin ? user?.branchId || "" : ""),
      salesPointId: salesPointId || user?.salesPointId || "",
      partyName: "",
      amount: 0,
      paymentMethod: "cash",
      narration: "",
    });
    setOpen(true);
  };

  return (
    <Box>
      <PageHeader
        title="Vouchers"
        subtitle="Payment, receipt and journal vouchers (appear in day-book / Tally export)"
        breadcrumbs={[
          { label: "Dashboard", to: "/dashboard" },
          { label: "Vouchers" },
        ]}
        action={
          canCreate("vouchers") || canCreate("reports") ? (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={openCreate}
            >
              New voucher
            </Button>
          ) : undefined
        }
      />

      {alert && (
        <Alert
          severity={alert.type}
          sx={{ mb: 2 }}
          onClose={() => setAlert(null)}
        >
          {alert.message}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {(error as Error).message || "Failed to load vouchers"}
        </Alert>
      )}

      <DataListCard
        footer={
          <TablePagination
            component="div"
            count={vouchers.length}
            page={page}
            onPageChange={(_, next) => setPage(next)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[10, 25, 50]}
          />
        }
      >
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              type="date"
              label="From"
              InputLabelProps={{ shrink: true }}
              value={startDate}
              onChange={(e) => {
                setStartDate(e.target.value);
                setPage(0);
              }}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              type="date"
              label="To"
              InputLabelProps={{ shrink: true }}
              value={endDate}
              onChange={(e) => {
                setEndDate(e.target.value);
                setPage(0);
              }}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <FormControl fullWidth>
              <InputLabel>Type</InputLabel>
              <Select
                label="Type"
                value={typeFilter}
                onChange={(e) => {
                  setTypeFilter(e.target.value);
                  setPage(0);
                }}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="receipt">Receipt</MenuItem>
                <MenuItem value="payment">Payment</MenuItem>
                <MenuItem value="journal">Journal</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          {!isAdmin && (
            <Grid item xs={12} sm={3}>
              <FormControl fullWidth>
                <InputLabel>Branch</InputLabel>
                <Select
                  label="Branch"
                  value={branchId}
                  onChange={(e) => {
                    setBranchId(e.target.value);
                    setSalesPointId("");
                    setPage(0);
                  }}
                >
                  <MenuItem value="">All</MenuItem>
                  {branches.map((b) => (
                    <MenuItem key={b.id} value={b.id}>
                      {b.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}
        </Grid>

        <TableContainer>
          <Table size="small" sx={{ width: "100%" }}>
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Voucher #</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Party</TableCell>
                <TableCell align="right">Amount</TableCell>
                <TableCell>Method</TableCell>
                <TableCell>Narration</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={7}>Loading…</TableCell>
                </TableRow>
              ) : vouchers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7}>No vouchers in this period.</TableCell>
                </TableRow>
              ) : (
                vouchers
                  .slice(page * rowsPerPage, (page + 1) * rowsPerPage)
                  .map((v) => (
                    <TableRow key={v.id}>
                      <TableCell>{String(v.voucherDate).slice(0, 10)}</TableCell>
                      <TableCell>{v.voucherNumber}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={String(v.voucherType).toUpperCase()}
                          color={typeColor(String(v.voucherType)) as any}
                        />
                      </TableCell>
                      <TableCell>{v.partyName || "—"}</TableCell>
                      <TableCell align="right">
                        {formatCurrency(v.amount)}
                      </TableCell>
                      <TableCell>{v.paymentMethod || "—"}</TableCell>
                      <TableCell>{v.narration || "—"}</TableCell>
                    </TableRow>
                  ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </DataListCard>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h6" fontWeight={700}>New voucher</Typography>
            <IconButton size="small" onClick={() => setOpen(false)}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent dividers sx={{ maxHeight: "72vh" }}>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <FormControl fullWidth>
              <InputLabel>Type</InputLabel>
              <Select
                label="Type"
                value={form.voucherType}
                onChange={(e) =>
                  setForm((p) => ({
                    ...p,
                    voucherType: e.target.value as VoucherType,
                  }))
                }
              >
                <MenuItem value="receipt">Receipt</MenuItem>
                <MenuItem value="payment">Payment</MenuItem>
                <MenuItem value="journal">Journal</MenuItem>
              </Select>
            </FormControl>
            <TextField
              fullWidth
              type="date"
              label="Date"
              InputLabelProps={{ shrink: true }}
              value={form.voucherDate}
              onChange={(e) =>
                setForm((p) => ({ ...p, voucherDate: e.target.value }))
              }
            />
            <FormControl fullWidth>
              <InputLabel>Branch</InputLabel>
              <Select
                label="Branch"
                value={form.branchId}
                onChange={(e) =>
                  setForm((p) => ({
                    ...p,
                    branchId: e.target.value,
                    salesPointId: "",
                  }))
                }
              >
                {branches.map((b) => (
                  <MenuItem key={b.id} value={b.id}>
                    {b.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Sales point</InputLabel>
              <Select
                label="Sales point"
                value={form.salesPointId}
                onChange={(e) =>
                  setForm((p) => ({ ...p, salesPointId: e.target.value }))
                }
              >
                {filteredSalesPoints.map((s) => (
                  <MenuItem key={s.id} value={s.id}>
                    {s.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              fullWidth
              label="Party name"
              value={form.partyName}
              onChange={(e) =>
                setForm((p) => ({ ...p, partyName: e.target.value }))
              }
            />
            <TextField
              fullWidth
              type="number"
              label="Amount (₹)"
              value={form.amount || ""}
              onChange={(e) =>
                setForm((p) => ({ ...p, amount: Number(e.target.value) }))
              }
              inputProps={{ min: 0, step: "0.01" }}
            />
            <FormControl fullWidth>
              <InputLabel>Payment method</InputLabel>
              <Select
                label="Payment method"
                value={form.paymentMethod || "cash"}
                onChange={(e) =>
                  setForm((p) => ({ ...p, paymentMethod: e.target.value }))
                }
              >
                <MenuItem value="cash">Cash</MenuItem>
                <MenuItem value="upi">UPI</MenuItem>
                <MenuItem value="card">Card</MenuItem>
                <MenuItem value="bank">Bank</MenuItem>
              </Select>
            </FormControl>
            <TextField
              fullWidth
              label="Narration"
              multiline
              minRows={2}
              value={form.narration}
              onChange={(e) =>
                setForm((p) => ({ ...p, narration: e.target.value }))
              }
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleCreate}
            disabled={saving}
          >
            {saving ? "Saving…" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default VouchersPage;
